import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const VideoGenerationStudio = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('image-to-video');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Common parameters
  const [formData, setFormData] = useState({
    // Image-to-Video
    init_image: '',
    
    // Text-to-Video
    prompt: '',
    negative_prompt: '',
    
    // Video parameters
    width: 1024,
    height: 576,
    num_frames: 25,
    fps: 6,
    motion_bucket_id: 127,
    noise_aug_strength: 0.02,
    cfg_scale: 2.5,
    seed: '',
    duration: 4,
    
    // Provider
    provider: 'stability_ai',
    model_id: ''
  });

  const providers = {
    'image-to-video': [
      { value: 'stability_ai', label: 'Stability AI (SVD)', models: ['stable-video-diffusion'] },
      { value: 'replicate', label: 'Replicate', models: ['stability-ai/stable-video-diffusion'] },
      { value: 'runwayml', label: 'RunwayML Gen-2', models: ['gen-2'] }
    ],
    'text-to-video': [
      { value: 'runwayml', label: 'RunwayML Gen-2', models: ['gen-2'] },
      { value: 'replicate', label: 'Replicate', models: ['zeroscope', 'modelscope'] }
    ],
    'video-to-video': [
      { value: 'runwayml', label: 'RunwayML', models: ['gen-2'] },
      { value: 'replicate', label: 'Replicate', models: ['video-to-video'] }
    ]
  };

  const aspectRatios = [
    { label: '16:9', width: 1024, height: 576 },
    { label: '9:16', width: 576, height: 1024 },
    { label: '1:1', width: 768, height: 768 },
    { label: '4:3', width: 1024, height: 768 },
    { label: '21:9', width: 1024, height: 440 }
  ];

  const durationOptions = [
    { value: 2, label: '2 seconds' },
    { value: 4, label: '4 seconds' },
    { value: 6, label: '6 seconds' },
    { value: 8, label: '8 seconds' },
    { value: 10, label: '10 seconds' }
  ];

  // Fetch generated videos
  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/media/assets', {
        params: { type: 'video', per_page: 50 }
      });
      setGeneratedVideos(response.data.items || []);
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Poll for job status
  useEffect(() => {
    if (jobs.length === 0) return;

    const pendingJobs = jobs.filter(j => j.status !== 'completed' && j.status !== 'failed');
    if (pendingJobs.length === 0) return;

    const interval = setInterval(async () => {
      for (const job of pendingJobs) {
        try {
          const response = await api.get(`/media/jobs/${job.job_id}`);
          setJobs(prev => prev.map(j => 
            j.job_id === job.job_id ? { ...j, ...response.data } : j
          ));
          
          if (response.data.status === 'completed') {
            setSuccess('Video generated successfully!');
            fetchVideos();
          } else if (response.data.status === 'failed') {
            setError(`Generation failed: ${response.data.error}`);
          }
        } catch (err) {
          console.error('Error polling job:', err);
        }
      }
    }, 5000); // Poll every 5 seconds for videos (they take longer)

    return () => clearInterval(interval);
  }, [jobs, fetchVideos]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleAspectRatio = (ratio) => {
    setFormData(prev => ({
      ...prev,
      width: ratio.width,
      height: ratio.height
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setFormData(prev => ({
          ...prev,
          init_image: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedVideo(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateImageToVideo = async () => {
    if (!formData.init_image) {
      setError('Please upload an image');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const params = {
        init_image: formData.init_image,
        prompt: formData.prompt || undefined,
        negative_prompt: formData.negative_prompt || undefined,
        width: formData.width,
        height: formData.height,
        num_frames: formData.num_frames,
        fps: formData.fps,
        motion_bucket_id: formData.motion_bucket_id,
        noise_aug_strength: formData.noise_aug_strength,
        cfg_scale: formData.cfg_scale,
        seed: formData.seed ? parseInt(formData.seed) : undefined,
        duration: formData.duration,
        provider: formData.provider,
        model_id: formData.model_id || undefined
      };

      const response = await api.post('/media/generate/video/from-image', params);
      
      setJobs(prev => [...prev, {
        job_id: response.data.job_id,
        status: response.data.status,
        type: 'image-to-video',
        created_at: new Date().toISOString()
      }]);
      
      setSuccess('Video generation started! This may take a few minutes.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start video generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateTextToVideo = async () => {
    if (!formData.prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const params = {
        prompt: formData.prompt,
        negative_prompt: formData.negative_prompt || undefined,
        width: formData.width,
        height: formData.height,
        num_frames: formData.num_frames,
        fps: formData.fps,
        cfg_scale: formData.cfg_scale,
        seed: formData.seed ? parseInt(formData.seed) : undefined,
        duration: formData.duration,
        provider: formData.provider,
        model_id: formData.model_id || undefined
      };

      const response = await api.post('/media/generate/video/from-text', params);
      
      setJobs(prev => [...prev, {
        job_id: response.data.job_id,
        status: response.data.status,
        type: 'text-to-video',
        created_at: new Date().toISOString()
      }]);
      
      setSuccess('Video generation started! This may take a few minutes.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start video generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = () => {
    if (activeTab === 'image-to-video') {
      handleGenerateImageToVideo();
    } else if (activeTab === 'text-to-video') {
      handleGenerateTextToVideo();
    } else if (activeTab === 'video-to-video') {
      // TODO: Implement video-to-video
      setError('Video-to-video generation coming soon!');
    }
  };

  const handleDownload = async (video) => {
    try {
      const response = await api.get(`/media/assets/${video.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = video.file_name || `video-${video.id}.mp4`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      // Fallback: open in new tab
      if (video.file_url) {
        window.open(video.file_url, '_blank');
      }
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await api.delete(`/media/assets/${videoId}`);
      setGeneratedVideos(prev => prev.filter(v => v.id !== videoId));
      if (selectedVideo?.id === videoId) {
        setSelectedVideo(null);
      }
      setSuccess('Video deleted successfully');
    } catch (err) {
      setError('Failed to delete video');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingJobs = jobs.filter(j => j.status !== 'completed' && j.status !== 'failed');

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Video Generation</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #8B5CF6'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🎬 Video Generation Studio
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Create AI-generated videos from images, text, or other videos
            </p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {error}
            <button 
              onClick={() => setError(null)}
              style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', fontSize: '1rem' }}
            >
              ✕
            </button>
          </div>
        )}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {success}
            <button 
              onClick={() => setSuccess(null)}
              style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', fontSize: '1rem' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Main Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '380px 1fr 320px',
          gap: '1.5rem',
          minHeight: '600px'
        }}>
          {/* Left Panel - Controls */}
          <div style={{
            backgroundColor: 'var(--gray-50)',
            borderRadius: '8px',
            border: '1px solid var(--gray-200)',
            padding: '1rem',
            overflowY: 'auto'
          }}>
            {/* Mode Tabs */}
            <div style={{
              display: 'flex',
              gap: '4px',
              marginBottom: '1rem',
              padding: '4px',
              backgroundColor: 'var(--gray-100)',
              borderRadius: '8px'
            }}>
              <button
                className={activeTab === 'image-to-video' ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ flex: 1, fontSize: '12px', padding: '8px 4px' }}
                onClick={() => setActiveTab('image-to-video')}
              >
                🖼️ Image→Video
              </button>
              <button
                className={activeTab === 'text-to-video' ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ flex: 1, fontSize: '12px', padding: '8px 4px' }}
                onClick={() => setActiveTab('text-to-video')}
              >
                ✍️ Text→Video
              </button>
              <button
                className={activeTab === 'video-to-video' ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ flex: 1, fontSize: '12px', padding: '8px 4px' }}
                onClick={() => setActiveTab('video-to-video')}
              >
                🎥 Video→Video
              </button>
            </div>

          {/* Image-to-Video Controls */}
            {activeTab === 'image-to-video' && (
              <>
                {/* Image Upload */}
                <div className="form-group">
                  <label className="form-label">Source Image</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  {uploadedImage ? (
                    <div>
                      <img 
                        src={uploadedImage} 
                        alt="Upload preview" 
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', marginBottom: '8px' }}
                      />
                      <button
                        className="btn btn-secondary"
                        style={{ width: '100%' }}
                        onClick={() => {
                          setUploadedImage(null);
                          setFormData(prev => ({...prev, init_image: ''}));
                        }}
                      >
                        Remove & Upload New
                      </button>
                    </div>
                  ) : (
                    <div 
                      style={{
                        border: '2px dashed var(--gray-300)',
                        borderRadius: '8px',
                        padding: '2rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDark ? '#374151' : 'white'
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📤</div>
                      <div style={{ color: 'var(--gray-600)', marginBottom: '4px' }}>Click or drag to upload</div>
                      <div style={{ color: 'var(--gray-400)', fontSize: '12px' }}>PNG, JPG, WebP up to 10MB</div>
                    </div>
                  )}
                </div>

                {/* Optional Motion Prompt */}
                <div className="form-group">
                  <label className="form-label">
                    Motion Prompt <span style={{ color: 'var(--gray-400)', fontWeight: 'normal' }}>(optional)</span>
                  </label>
                  <textarea
                    name="prompt"
                    value={formData.prompt}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Describe how you want the image to animate..."
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Text-to-Video Controls */}
            {activeTab === 'text-to-video' && (
              <>
                <div className="form-group">
                  <label className="form-label">Prompt</label>
                  <textarea
                    name="prompt"
                    value={formData.prompt}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Describe the video you want to generate..."
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Negative Prompt <span style={{ color: 'var(--gray-400)', fontWeight: 'normal' }}>(optional)</span>
                  </label>
                  <textarea
                    name="negative_prompt"
                    value={formData.negative_prompt}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="What to avoid in the video..."
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Video-to-Video Controls */}
            {activeTab === 'video-to-video' && (
              <>
                <div className="form-group">
                  <label className="form-label">Source Video</label>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    style={{ display: 'none' }}
                  />
                  {uploadedVideo ? (
                    <div>
                      <video 
                        src={uploadedVideo}
                        style={{ width: '100%', maxHeight: '160px', borderRadius: '8px', marginBottom: '8px' }}
                        controls
                      />
                      <button
                        className="btn btn-secondary"
                        style={{ width: '100%' }}
                        onClick={() => setUploadedVideo(null)}
                      >
                        Remove & Upload New
                      </button>
                    </div>
                  ) : (
                    <div 
                      style={{
                        border: '2px dashed var(--gray-300)',
                        borderRadius: '8px',
                        padding: '2rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDark ? '#374151' : 'white'
                      }}
                      onClick={() => videoInputRef.current?.click()}
                    >
                      <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎥</div>
                      <div style={{ color: 'var(--gray-600)', marginBottom: '4px' }}>Click or drag to upload</div>
                      <div style={{ color: 'var(--gray-400)', fontSize: '12px' }}>MP4, WebM up to 50MB</div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Style Transfer Prompt</label>
                  <textarea
                    name="prompt"
                    value={formData.prompt}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Describe the style to apply..."
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Common Controls */}
            <div className="form-group">
              <label className="form-label">Provider</label>
              <select
                name="provider"
                value={formData.provider}
                onChange={handleInputChange}
                className="form-select"
              >
                {(providers[activeTab] || []).map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Aspect Ratio</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {aspectRatios.map(ratio => (
                  <button
                    key={ratio.label}
                    className={formData.width === ratio.width && formData.height === ratio.height ? 'btn btn-primary' : 'btn btn-secondary'}
                    style={{ padding: '8px', fontSize: '13px' }}
                    onClick={() => handleAspectRatio(ratio)}
                  >
                    {ratio.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Duration</label>
              <select
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className="form-select"
              >
                {durationOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Advanced Options Toggle */}
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 0',
                cursor: 'pointer',
                borderTop: '1px solid var(--gray-200)',
                marginTop: '1rem'
              }}
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--gray-600)' }}>
                ⚙️ Advanced Options
              </span>
              <span>{showAdvanced ? '▲' : '▼'}</span>
            </div>

            {showAdvanced && (
              <>
                {activeTab === 'image-to-video' && (
                  <div className="form-group">
                    <label className="form-label">
                      Motion Amount: {formData.motion_bucket_id}
                    </label>
                    <input
                      type="range"
                      name="motion_bucket_id"
                      min="1"
                      max="255"
                      value={formData.motion_bucket_id}
                      onChange={handleInputChange}
                      className="form-range"
                      style={{ width: '100%' }}
                    />
                    <div style={{ textAlign: 'right', fontSize: '12px', color: 'var(--gray-500)' }}>Less ← Motion → More</div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Frames Per Second: {formData.fps}</label>
                  <input
                    type="range"
                    name="fps"
                    min="1"
                    max="30"
                    value={formData.fps}
                    onChange={handleInputChange}
                    className="form-range"
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">CFG Scale: {formData.cfg_scale}</label>
                  <input
                    type="range"
                    name="cfg_scale"
                    min="1"
                    max="10"
                    step="0.1"
                    value={formData.cfg_scale}
                    onChange={handleInputChange}
                    className="form-range"
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Seed <span style={{ color: 'var(--gray-400)', fontWeight: 'normal' }}>(leave empty for random)</span>
                  </label>
                  <input
                    type="number"
                    name="seed"
                    value={formData.seed}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Random"
                  />
                </div>
              </>
            )}

            {/* Generate Button */}
            <button
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '1rem', padding: '12px' }}
              onClick={handleGenerate}
              disabled={generating || (!formData.init_image && activeTab === 'image-to-video') || (!formData.prompt && activeTab === 'text-to-video')}
            >
              {generating ? (
                <>
                  <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px' }}></span>
                  Generating...
                </>
              ) : (
                <>🎬 Generate Video</>
              )}
            </button>
          </div>

          {/* Center Panel - Gallery & Jobs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Active Jobs */}
            {pendingJobs.length > 0 && (
              <div style={{
                backgroundColor: 'var(--gray-50)',
                borderRadius: '8px',
                border: '1px solid var(--gray-200)',
                padding: '1rem'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '12px', color: 'var(--gray-700)' }}>
                  ⏳ Active Jobs ({pendingJobs.length})
                </div>
                {pendingJobs.map(job => (
                  <div key={job.job_id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px',
                    backgroundColor: isDark ? '#374151' : 'white',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}>
                    <span className={`badge badge-${job.status === 'processing' ? 'warning' : 'secondary'}`}>
                      {job.status}
                    </span>
                    <div style={{ flex: 1, height: '6px', backgroundColor: 'var(--gray-200)', borderRadius: '3px' }}>
                      <div 
                        style={{
                          height: '100%',
                          borderRadius: '3px',
                          backgroundColor: 'var(--primary-color)',
                          width: job.progress ? `${job.progress}%` : job.status === 'processing' ? '50%' : '10%',
                          transition: 'width 0.3s'
                        }}
                      />
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                      {job.type}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Video Gallery */}
            <div style={{
              backgroundColor: 'var(--gray-50)',
              borderRadius: '8px',
              border: '1px solid var(--gray-200)',
              padding: '1rem',
              flex: 1,
              overflowY: 'auto'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
                🎥 Generated Videos ({generatedVideos.length})
              </div>
              
              {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <span className="spinner-border"></span>
                  <div style={{ marginTop: '1rem', color: 'var(--gray-500)' }}>Loading videos...</div>
                </div>
              ) : generatedVideos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'var(--gray-700)' }}>No videos yet</div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                    Generate your first video using the controls on the left
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '1rem'
                }}>
                  {generatedVideos.map(video => (
                    <div
                      key={video.id}
                      style={{
                        backgroundColor: isDark ? '#1f2937' : 'white',
                        borderRadius: '8px',
                        border: selectedVideo?.id === video.id ? '2px solid var(--primary-color)' : '1px solid var(--gray-200)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setSelectedVideo(video)}
                    >
                      <div style={{
                        aspectRatio: '16/9',
                        backgroundColor: 'var(--gray-100)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}>
                        {video.thumbnail_url ? (
                          <img 
                            src={video.thumbnail_url} 
                            alt={video.title || 'Video thumbnail'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <span style={{ fontSize: '2rem' }}>🎥</span>
                        )}
                        <span style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}>
                          {formatDuration(video.duration)}
                        </span>
                      </div>
                      <div style={{ padding: '8px' }}>
                        <div style={{
                          fontWeight: '500',
                          fontSize: '13px',
                          color: 'var(--gray-800)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {video.title || `Video #${video.id}`}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                          {formatDate(video.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Details */}
          <div style={{
            backgroundColor: 'var(--gray-50)',
            borderRadius: '8px',
            border: '1px solid var(--gray-200)',
            padding: '1rem',
            overflowY: 'auto'
          }}>
            {selectedVideo ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Video Details</h3>
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className="breadcrumb-link"
                    style={{ padding: 0 }}
                  >
                    ✕
                  </button>
                </div>

                {/* Video Player */}
                {selectedVideo.file_url && (
                  <video 
                    src={selectedVideo.file_url}
                    style={{ width: '100%', borderRadius: '8px', backgroundColor: 'var(--gray-100)' }}
                    controls
                    autoPlay
                    loop
                  />
                )}

                {/* Metadata */}
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--gray-500)', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Properties
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    <div style={{ padding: '8px', backgroundColor: isDark ? '#374151' : 'white', borderRadius: '6px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Duration</div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{formatDuration(selectedVideo.duration)}</div>
                    </div>
                    <div style={{ padding: '8px', backgroundColor: isDark ? '#374151' : 'white', borderRadius: '6px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Resolution</div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{selectedVideo.width} × {selectedVideo.height}</div>
                    </div>
                    <div style={{ padding: '8px', backgroundColor: isDark ? '#374151' : 'white', borderRadius: '6px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Generator</div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{selectedVideo.generator || 'SVD'}</div>
                    </div>
                    <div style={{ padding: '8px', backgroundColor: isDark ? '#374151' : 'white', borderRadius: '6px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Created</div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{formatDate(selectedVideo.created_at)}</div>
                    </div>
                  </div>
                </div>

                {/* Prompt */}
                {selectedVideo.prompt && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--gray-500)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Prompt
                    </div>
                    <div style={{
                      backgroundColor: isDark ? '#374151' : 'white',
                      borderRadius: '6px',
                      padding: '10px',
                      fontSize: '13px',
                      color: 'var(--gray-600)',
                      lineHeight: '1.5'
                    }}>
                      {selectedVideo.prompt}
                    </div>
                  </div>
                )}

                {/* Generation Parameters */}
                {selectedVideo.generation_params && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--gray-500)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Parameters
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {Object.entries(selectedVideo.generation_params || {}).map(([key, value]) => (
                        <div key={key} style={{ padding: '8px', backgroundColor: isDark ? '#374151' : 'white', borderRadius: '6px' }}>
                          <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{key.replace(/_/g, ' ')}</div>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{String(value)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Export Options */}
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--gray-500)', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Export
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(selectedVideo)}>
                      📥 MP4
                    </button>
                    <button className="btn btn-secondary btn-sm">
                      🔄 WebM
                    </button>
                    <button className="btn btn-secondary btn-sm">
                      🎞️ GIF
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
                  <button 
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    onClick={() => handleDownload(selectedVideo)}
                  >
                    📥 Download
                  </button>
                  <button 
                    className="btn btn-danger"
                    style={{ flex: 1 }}
                    onClick={() => handleDelete(selectedVideo.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👆</div>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: 'var(--gray-700)' }}>Select a video</div>
                <div style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                  Click on a video to view details and export options
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoGenerationStudio;
